import types from '../mutation-types';
import Vue from 'vue';
import ContactNotesAPI from '../../api/contactNotes';

export const state = {
  records: {},
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isDeleting: false,
  },
};

export const getters = {
  getAllNotesByContact: _state => contactId => {
    const record = _state.records[contactId];
    const notes = record ? record.data : [];
    return notes.sort((r1, r2) => r2.updated_at - r1.updated_at);
  },
  getMetaByContact: _state => contactId => {
    return _state.records[contactId]
      ? _state.records[contactId].meta
      : { currentPage: 1, hasMore: false };
  },
  getUIFlags(_state) {
    return _state.uiFlags;
  },
};

export const actions = {
  async get(
    { commit, state: _state },
    { contactId, page = 1, start_date, end_date }
  ) {
    commit(types.SET_CONTACT_NOTES_UI_FLAG, { isFetching: true });
    try {
      const { data } = await ContactNotesAPI.get(contactId, {
        page,
        per_page: 10,
        start_date,
        end_date,
      });
      // A resposta agora é { meta, payload }
      commit(types.SET_CONTACT_NOTES, { contactId, data, page });
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_CONTACT_NOTES_UI_FLAG, { isFetching: false });
    }
  },

  async create({ commit }, { contactId, content, attachments }) {
    commit(types.SET_CONTACT_NOTES_UI_FLAG, { isCreating: true });
    try {
      const { data } = await ContactNotesAPI.create(contactId, {
        content,
        attachments,
      });
      commit(types.ADD_CONTACT_NOTE, { contactId, data });
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_CONTACT_NOTES_UI_FLAG, { isCreating: false });
    }
  },

  async update(
    { commit },
    { contactId, id, content, attachments, attachmentsToRemove }
  ) {
    commit(types.SET_CONTACT_NOTES_UI_FLAG, { isCreating: true });
    try {
      const { data } = await ContactNotesAPI.update(contactId, id, {
        content,
        attachments,
        attachmentsToRemove,
      });
      commit(types.EDIT_CONTACT_NOTE, { contactId, data });
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_CONTACT_NOTES_UI_FLAG, { isCreating: false });
    }
  },

  async delete({ commit }, { noteId, contactId }) {
    commit(types.SET_CONTACT_NOTES_UI_FLAG, { isDeleting: true });
    try {
      await ContactNotesAPI.delete(contactId, noteId);
      commit(types.DELETE_CONTACT_NOTE, { contactId, noteId });
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_CONTACT_NOTES_UI_FLAG, { isDeleting: false });
    }
  },
};

export const mutations = {
  [types.SET_CONTACT_NOTES_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },

  [types.SET_CONTACT_NOTES]($state, { data: responseData, contactId, page }) {
    // responseData = { meta: { current_page, total_pages, total_count }, payload: [...] }
    const { meta, payload: notes } = responseData;
    const hasMore = meta.current_page < meta.total_pages;

    if (page === 1 || !$state.records[contactId]) {
      Vue.set($state.records, contactId, {
        data: notes,
        meta: { currentPage: meta.current_page, hasMore },
      });
    } else {
      const existingData = $state.records[contactId].data;
      // Evitar duplicatas
      const newNotes = notes.filter(
        note => !existingData.find(n => n.id === note.id)
      );
      Vue.set($state.records, contactId, {
        data: [...existingData, ...newNotes],
        meta: { currentPage: meta.current_page, hasMore },
      });
    }
  },
  [types.ADD_CONTACT_NOTE]($state, { data, contactId }) {
    const record = $state.records[contactId] || {
      data: [],
      meta: { currentPage: 1, hasMore: false },
    };
    const contactNotes = record.data;
    Vue.set($state.records, contactId, {
      ...record,
      data: [...contactNotes, data],
    });
  },
  [types.EDIT_CONTACT_NOTE]($state, { data, contactId }) {
    const record = $state.records[contactId];
    if (record) {
      const contactNotes = record.data;
      const index = contactNotes.findIndex(note => note.id === data.id);
      if (index > -1) {
        const newNotes = [...contactNotes];
        newNotes[index] = data;
        Vue.set($state.records, contactId, {
          ...record,
          data: newNotes,
        });
      }
    }
  },
  [types.DELETE_CONTACT_NOTE]($state, { noteId, contactId }) {
    const record = $state.records[contactId];
    if (record) {
      const contactNotes = record.data;
      const withoutDeletedNote = contactNotes.filter(
        note => note.id !== noteId
      );
      Vue.set($state.records, contactId, {
        ...record,
        data: withoutDeletedNote,
      });
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
